<script setup>
const fullWidthMode = inject("fullWidthMode", false);
const halfCol = computed(() => (fullWidthMode ? undefined : 6));
import { splitGg } from '@/composables/splitSentence';

import PersonalFiveChart from '@/components/saju-insight/personality/PersonalFiveChart.vue';
import SajuMyeongsik from '@/components/saju/SajuMyeongsik.vue';
import { useAnalysisStore } from '@/store/modules/analysis';
import BigLuckTimeline from './BigLuckTimeline.vue';

const props = defineProps({
  yData: Object,
  pSubData: Object,
  userName: String,
  isMobile: Boolean,
  birthData: Object,
  birthJijanggan: String,
  langMode: String || 'CHNKOR',
  birthTitle: String,
  transdata: Object,
  isActive: Boolean,
  sajuData: Object,
});

const store = useAnalysisStore();
const sajuName = computed(() => store.userName);

const curDaeun = computed(() => {
  if (!props.sajuData?.daeuns || !props.sajuData?.selectdaeun) {
    return props.sajuData?.curDaeun || {};
  }

  // sajuData.daeuns.sky에서 num이 sajuData.selectdaeun과 일치하는 객체 찾기
  const matchingSky = props.sajuData.daeuns.sky.find(
    (item) => item.num === props.sajuData.selectdaeun,
  );
  const matchingEarth = props.sajuData.daeuns.earth.find(
    (item) => item.num === props.sajuData.selectdaeun,
  );

  if (matchingSky && matchingEarth) {
    return {
      sky: [matchingSky],
      earth: [matchingEarth],
    };
  }

  return props.sajuData?.curDaeun || {};
});

// 용신 데이터
const selectedYong = computed(() => props.yData?.분석상세?.선정용신?.분석용신);
const yongsinElement = computed(() => props.yData?.분석상세?.선정용신?.용신원소);
const yongLabel = computed(() => fiveMap[selectedYong.value]?.hanja || '');
const yongClass = computed(() => fiveMap[selectedYong.value]?.class || '');
const selectedType = computed(() => props.yData?.분석상세?.선정용신?.분석유형);
const confideceLevel = computed(() => props.yData?.분석상세?.선정용신?.용신확률);
const hanChartColor = computed(() => props.yData?.분석상세?.용희기구한분석?.오행분류); // 차트컬러
const fiveMap = {
  화: { hanja: '火', class: 'five01' },
  수: { hanja: '水', class: 'five02' },
  목: { hanja: '木', class: 'five03' },
  금: { hanja: '金', class: 'five04' },
  토: { hanja: '土', class: 'five05' },
  '-': { hanja: '-', class: 'none' },
};
// 격국
const ggName = computed(() => splitGg(props.yData?.분석상세?.격국분석?.격국이름));
const ggType = computed(() => props.yData?.분석상세?.격국분석?.격국유형);
// 음양
const yinyangStatus = computed(() => props.yData?.분석상세?.음양조후?.음양상태);
const yinyangChartData = computed(() => {
  const val = props.yData?.분석상세?.음양조후?.음양비율;
  return typeof val === 'string' ? val.replace(/^[-+]/, '') : val;
});
const yinyangClass = computed(() => {
  if (yinyangChartData.value?.startsWith('+')) return 'st-yang';
  return 'st-yin';
});
// 조후
const jhStatus = computed(() => props.yData?.분석상세?.음양조후?.조후상태);
const jhChartData = computed(() => props.yData?.분석상세?.음양조후?.조후비율);
const jhDisplayData = computed(() => {
  const val = jhChartData.value;
  return typeof val === 'string' ? val.replace(/^[-+]/, '') : val;
});
const johooClass = computed(() => {
  if (jhChartData.value?.startsWith('+')) return 'st-jo';
  return 'st-hoo';
});
// 신강/신약
const ssTitle = computed(() => props.yData?.분석상세?.신강신약여부?.신강여부);
const ssSubTitle = computed(() => props.yData?.분석상세?.신강신약여부?.신강여부해석);
// 대운
const changeDetail = computed(() => props.yData?.분석상세?.대운흐름?.전환상세);
// TODO: 천간지지 작업 필요
const refinedTransitionList = computed(() => {
  return changeDetail.value.map(([ganji, age, from, to, year]) => ({
    age,
    from,
    to,
    year,
  }));
});
const bigLuckFlowDesc = computed(() => props.yData?.분석상세?.대운흐름?.대운흐름요약);

/** 차트 데이터 */
//const chartData = computed(() => props.yData?.종합분석 || props.pSubData)
const chartData = computed(() => props.yData?.분석상세?.오행분석);

// 오행 분석 텍스트를 동적으로 생성하는 computed
const fiveElementsAnalysis = computed(() => {
  const analysisText = chartData.value?.오행분석 || '';

  // **별표로 감싸진 오행만 찾아서 span으로 변환
  const starPattern = /\*\*([목화토금수])\*\*/g;

  // 오행별 클래스 매핑
  const elementClassMap = {
    목: 'txt-five03',
    화: 'txt-five01',
    토: 'txt-five05',
    금: 'txt-five04',
    수: 'txt-five02',
  };

  // **별표로 감싸진 오행을 span으로 변환
  let resultText = analysisText.replace(starPattern, (match, element) => {
    const elementClass = elementClassMap[element];
    return `<span class="${elementClass} txt-bold">"${element}"</span>`;
  });

  // ** ** 패턴을 txt-bold 클래스로 감싸는 로직 추가
  const boldPattern = /\*\*([^*]+)\*\*/g;
  resultText = resultText.replace(boldPattern, (match, text) => {
    return `<span class="txt-bold">"${text}"</span>`;
  });

  return resultText;
});
</script>

<template>
  <VRow class="full-height">
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-first">
      <VCard class="personality-card full-height right">
        <div class="detail-card__top-title yongsin content_chapter_area_title">
          <span class="detail-tit__num yongsin">1</span>
          <span class="detail-tit__txt yongsin">종합분석</span>
        </div>
        <div class="personality-wrapper with-tit st-gap-24 content_info_area_box">
          <div class="person-content-box youngsin-manse-birth-box">
            <div class="tit-section">
              <!-- <div v-for="(str, idx) in mainTitle" :key="idx" class="person-main-title">
                {{ str }}
              </div> -->
              <!-- <div class="person-main-title">{{ sajuName }}님의 낭만 용신 분석</div> -->
              <div class="content__title">🌏 사주 원국</div>
              <!-- <div class="main-title__underline" /> -->
            </div>

            <SajuMyeongsik
              ref="ManseBirthMainArea"
              :pr-birth-data="birthData"
              :pr-birth-jijanggan="birthJijanggan"
              :pr-lang-mode="langMode"
              :pr-birth-title="birthTitle"
              :pr-transdata="transdata"
              :pr-type="'birth'"
              :pr-cur-daeun="curDaeun"
              :is-active="isActive"
            />
          </div>

          <div class="person-content-box">
            <div class="ys-content-box">
              <div class="content__title">📊 오행 분포</div>
              <div class="person-content-box chart bar-line-chart">
                <PersonalFiveChart :chartData="chartData" :chartType="'jijangFive'" />
              </div>
              <div class="desc__body gray" v-html="fiveElementsAnalysis"></div>
            </div>
          </div>
        </div>
      </VCard>
    </VCol>
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-second">
      <VCard class="personality-card full-height">
        <div class="personality-wrapper content_info_area_box">
          <div class="ys-content-box">
            <div class="ys-0101-box" :class="yongClass">
              <div class="ys-0101-top">
                <div class="ys-0101-box__title" :class="yongClass">{{ yongLabel }}</div>
                <div class="ys-0101-box__desc">
                  <div class="txt-box st1">
                    <div class="txt1">용신</div>
                    <div class="txt2">{{ yongLabel }}, {{ yongsinElement }}</div>
                  </div>
                  <div class="txt-box st2">
                    <div class="txt1">분석유형</div>
                    <div class="txt2">{{ selectedType }}</div>
                  </div>
                  <div class="txt-box st3">
                    <div class="txt1">신뢰도</div>
                    <div class="txt2">{{ confideceLevel }}</div>
                  </div>
                </div>
              </div>
              <div class="ys-0101-bottom">
                <div class="han-txt-box" v-for="(element, key) in hanChartColor" :key="key">
                  <div class="han-txt1" :class="fiveMap[element]?.class">
                    {{ fiveMap[element]?.hanja || '-' }}
                  </div>
                  <div class="han-txt2">{{ key }}</div>
                </div>
              </div>
            </div>
            <div class="ys-0102-box">
              <div class="ys-0102-top">
                <div class="ys-0102-item">
                  <div class="txt3 left">격국</div>
                  <div class="txt1-box">
                    <div class="txt1">
                      {{ ggName[0] }}
                      <div v-if="ggName?.length === 2" class="txt1 sub">({{ ggName[1] }}</div>
                    </div>
                    <div class="txt2">{{ ggType }}</div>
                  </div>
                </div>
                <div class="ys-0102-item">
                  <div class="txt3 right">신강/신약</div>
                  <div class="txt1-box">
                    <div class="txt1">{{ ssTitle }}</div>
                    <div class="txt2">{{ ssSubTitle }}</div>
                  </div>
                </div>
              </div>
              <div class="ys-0102-bottom">
                <div class="ys-0102-item">
                  <div class="txt3 left">음양</div>
                  <div class="txt1-box">
                    <div class="txt1">{{ yinyangStatus }}</div>
                    <div class="txt2" :class="yinyangClass">{{ yinyangChartData || '' }}</div>
                  </div>
                </div>
                <div class="ys-0102-item">
                  <div class="txt3 right">조후</div>
                  <div class="txt1-box">
                    <div class="txt1">{{ jhStatus }}</div>
                    <div class="txt2" :class="johooClass">{{ jhDisplayData || '' }}</div>
                  </div>
                </div>
              </div>

              <div class="ys-0102-center" :class="yongClass">
                <div class="txt1">{{ yongLabel }}</div>
                <div class="txt2">용신</div>
              </div>
            </div>
          </div>
          <div class="ys-content-box">
            <div class="content__title">📍 대운 타임라인 주요 전환점</div>
            <BigLuckTimeline />
            <div class="desc__body gray mb-20">
              {{ bigLuckFlowDesc }}
            </div>
          </div>
        </div>
      </VCard>
    </VCol>
  </VRow>
</template>
<style lang="scss">
@import '@/assets/nangman_style/_util.scss';
@import '@/assets/nangman_style/youngsin.scss';
@import '@/assets/nangman_style/detailed-analysis.scss';

.ys-0102-box {
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  position: relative;
  .ys-0102-top {
    // display: flex;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    align-items: flex-start;
    gap: 8px;
    align-self: stretch;
  }
  .ys-0102-bottom {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    align-items: flex-start;
    gap: 8px;
    align-self: stretch;
  }
  .ys-0102-item {
    display: flex;
    // height: 100%;
    padding: 20px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 4px;
    flex: 1 0 0;
    border-radius: 12px;
    border: 1px solid var(--Gray-300, #ebebeb);
    background: var(--gray-100-white, #fff);
    .txt1-box {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      gap: 8px;
    }
    .txt1 {
      color: var(--Gray-800, #333);
      font-family: Pretendard;
      font-size: 18px;
      font-style: normal;
      font-weight: 700;
      line-height: 28px; /* 155.556% */
      display: inline;
    }
    .txt2 {
      display: flex;
      padding: 2px 8px;
      justify-content: center;
      align-items: center;
      gap: 10px;
      border-radius: 40px;
      background: var(--Gray-800, #333);
      color: var(--gray-100-white, #fff);
      font-family: Pretendard;
      font-size: 15px;
      font-style: normal;
      font-weight: 700;
      line-height: 24px;
      &.st-yin {
        color: var(--gray-100-white, #fff);
        background: var(--Gray-600, #757575);
      }
      &.st-yang {
        color: var(--Gray-800, #333);
        background: var(--Gray-300, #ebebeb);
      }
      &.st-jo {
        background: #ffd374;
        color: var(--Gray-800, #333);
      }
      &.st-hoo {
        background: #75a4fd !important;
        color: var(--gray-100-white, #fff);
      }
    }
    .txt3 {
      width: auto;
      color: var(--Gray-600, #757575);
      font-family: Pretendard;
      font-size: 14px;
      font-style: normal;
      font-weight: 500;
      line-height: 22px;
      &.left {
        margin-right: auto;
      }
      &.right {
        margin-left: auto;
      }
    }
  }
  .ys-0102-center {
    display: flex;
    width: 100px;
    padding: 17px 29px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 2px;
    aspect-ratio: 1/1;
    border-radius: 100px;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    .txt1 {
      color: var(--gray-100-white, #fff);
      text-align: center;
      font-family: ChosunGs;
      font-size: 42px;
      font-style: normal;
      font-weight: 400;
      line-height: 42px; /* 100% */
    }
    &.five01 {
      background: $fiveColor01;
    }
    &.five02 {
      background: $fiveColor02;
    }
    &.five03 {
      background: $fiveColor03;
    }
    &.five04 {
      background: $fiveColor04;
    }
    &.five05 {
      background: $fiveColor05;
    }
  }
}
.ys-0101-bottom {
  display: flex;
  padding: 16px 0;
  justify-content: center;
  align-items: center;
  align-self: stretch;
  border-radius: 12px;
  border: 1px solid var(--Gray-200, #f8f8f8);
  background: var(--gray-100-white, #fff);
  .han-txt-box {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    flex: 1 0 0;
    position: relative;
    &::after {
      content: '';
      position: absolute;
      margin-top: -10px;
      top: 50%;
      right: 0;
      width: 1px;
      height: 20px;
      background: var(--Gray-300, #ebebeb);
    }
    .han-txt1 {
      display: flex;
      width: 32px;
      height: 32px;
      padding: 4px;
      justify-content: center;
      align-items: center;
      aspect-ratio: 1/1;
      color: var(--gray-100-white, #fff);
      text-align: center;
      font-family: 'ChosunGs';
      font-size: 20px;
      font-style: normal;
      font-weight: 400;
      line-height: 24px; /* 120% */
      border-radius: 100%;
      position: relative;
      &.five01 {
        background: $fiveColor01;
      }
      &.five02 {
        background: $fiveColor02;
      }
      &.five03 {
        background: $fiveColor03;
      }
      &.five04 {
        background: $fiveColor04;
      }
      &.five05 {
        background: $fiveColor05;
      }
    }
    .han-txt2 {
      color: var(--Gray-800, #333);
      text-align: center;
      font-family: Pretendard;
      font-size: 15px;
      font-style: normal;
      font-weight: 700;
      line-height: 24px;
    }
  }
}
.ys-0101-box {
  display: flex;
  padding: 32px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 20px;
  align-self: stretch;
  border-radius: 12px;
  // border: 1px solid rgba(64, 64, 64, 0.2);
  // background: rgba(64, 64, 64, 0.05);
  &.five01 {
    border: 1px solid rgba(194, 53, 53, 0.2);
    background: rgba(194, 53, 53, 0.05);
  }
  &.five02 {
    border: 1px solid rgba(64, 64, 64, 0.2);
    background: rgba(64, 64, 64, 0.05);
  }
  &.five03 {
    border: 1px solid rgba(94, 159, 28, 0.2);
    background: rgba(94, 159, 28, 0.05);
  }
  &.five04 {
    border: 1px solid rgba(148, 148, 148, 0.2);
    background: rgba(148, 148, 148, 0.05);
  }
  &.five05 {
    border: 1px solid rgba(219, 152, 35, 0.2);
    background: rgba(219, 152, 35, 0.05);
  }
  .ys-0101-top {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 40px;
    align-self: stretch;
    .ys-0101-box__title {
      display: flex;
      width: 80px;
      height: 80px;
      padding: 17px;
      justify-content: center;
      align-items: center;
      aspect-ratio: 1/1;
      border-radius: 100px;
      color: var(--gray-100-white, #fff);
      text-align: center;
      font-family: 'ChosunGs';
      font-size: 46px;
      font-style: normal;
      font-weight: 400;
      line-height: 46px; /* 100% */
      &.five01 {
        background: $fiveColor01;
      }
      &.five02 {
        background: $fiveColor02;
      }
      &.five03 {
        background: $fiveColor03;
      }
      &.five04 {
        background: $fiveColor04;
      }
      &.five05 {
        background: $fiveColor05;
      }
    }
    .ys-0101-box__desc {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: flex-start;
      gap: 6px;
      .txt-box {
        display: flex;
        align-items: center;
        gap: 8px;
        .txt1 {
          color: var(--Gray-800, #333);
          font-family: Pretendard;
          font-size: 15px;
          font-style: normal;
          font-weight: 400;
          line-height: 24px; /* 160% */
        }
        .txt2 {
          color: var(--black-100, #404040);
          font-family: Pretendard;
          font-size: 18px;
          font-style: normal;
          font-weight: 700;
          line-height: 28px; /* 155.556% */
        }
        &.st1 {
          .txt2 {
            color: #404040;
          }
        }
        &.st2 {
          .txt2 {
            color: #4e80d6;
          }
        }
        &.st3 {
          .txt2 {
            color: #eb4c10;
          }
        }
      }
    }
  }
}

.desc__body.gray.mb-20 {
  margin-bottom: 20px;
}
</style>
